import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useUser } from './UserContext';
import rbacService from '../services/rbacService';
import { LOCKOUT_EVENT } from '../utils/lockout';

/**
 * PermissionsContext — session-wide RBAC gating (spec §3).
 *
 * After the user is authenticated, this fetches GET /user/me/permissions once and
 * exposes the effective permission set plus helpers to gate menus, routes and
 * buttons. `permissions` is already (roles ∩ company envelope) with the wildcard
 * expanded server-side, so the UI only needs a membership check.
 *
 * Rollout safety — fail OPEN:
 *   The gating helpers only ever HIDE something once we have a *successful*
 *   permissions payload (`ready === true`). While the call is in flight, or if it
 *   fails entirely (endpoint missing / network error), `has()` returns true so the
 *   app keeps working exactly as it did before RBAC shipped. We never lock a user
 *   out of the whole UI because of a failed bootstrap call.
 *
 * Partner tier (Partner tier guide §3): `tier`, `partner`, `isPartnerAdmin` and
 * `partnerPermissions` drive the Partner admin area. Unlike `has()`, the partner
 * check `hasPartner()` fails CLOSED — it only ever gates admin surfaces — and has
 * no platform-admin bypass (platform admins get `partner_permissions: []` and
 * manage partners from Platform Admin instead).
 *
 * Lockout (§9): a "Partner/Company/User … is deactivated" 403 anywhere sets
 * `lockout`, and the app swaps to a full-page blocking screen. The session is
 * kept so `retryAfterLockout()` restores access once reactivated.
 */
const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useUser();

  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [company, setCompany] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [tier, setTier] = useState('none');
  const [partner, setPartner] = useState(null);
  const [isPartnerAdmin, setIsPartnerAdmin] = useState(false);
  const [partnerPermissions, setPartnerPermissions] = useState([]);
  const [lockout, setLockout] = useState(null); // 'partner' | 'company' | 'user' | null
  const [ready, setReady] = useState(false); // true only after a successful load
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiKey = user?.api_key;
  const inFlightRef = useRef(false);

  const load = useCallback(async () => {
    if (!apiKey) return; // wait until we actually have an authenticated key
    if (inFlightRef.current) return; // never stack concurrent bootstraps
    inFlightRef.current = true;
    setLoading(true);
    try {
      const data = await rbacService.getMyPermissions();
      setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
      setRoles(Array.isArray(data?.roles) ? data.roles : []);
      setIsPlatformAdmin(Boolean(data?.is_platform_admin));
      setCompany(data?.company ?? null);
      setEntitlements(Array.isArray(data?.entitlements) ? data.entitlements : []);
      setTier(
        data?.tier ||
          (data?.is_platform_admin ? 'platform' : data?.company ? 'company' : 'none')
      );
      setPartner(data?.partner ?? null);
      setIsPartnerAdmin(Boolean(data?.is_partner_admin));
      setPartnerPermissions(
        Array.isArray(data?.partner_permissions) ? data.partner_permissions : []
      );
      setError(null);
      setReady(true);
    } catch (err) {
      // Fail open: a missing/unreachable endpoint must not break the app.
      // Leave `ready` false so gating helpers stay permissive.
      console.warn(
        'Permissions bootstrap failed — RBAC gating disabled for this session:',
        err?.message
      );
      setError(err);
      setReady(false);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [apiKey]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-fetch permissions when the API signals a 403 (spec §3.1: "re-fetch on 403
  // to pick up admin changes"). The axios interceptor dispatches this event.
  useEffect(() => {
    const handleRefresh = () => load();
    window.addEventListener('rbac:refresh', handleRefresh);
    return () => window.removeEventListener('rbac:refresh', handleRefresh);
  }, [load]);

  // Lockout 403s (§9) — raised by the axios interceptor and streamingService.
  useEffect(() => {
    const handleLockout = (event) => setLockout(event?.detail?.kind || 'user');
    window.addEventListener(LOCKOUT_EVENT, handleLockout);
    return () => window.removeEventListener(LOCKOUT_EVENT, handleLockout);
  }, []);

  /** "Try again" on the lockout screen: re-check; a still-locked session re-raises it. */
  const retryAfterLockout = useCallback(async () => {
    setLockout(null);
    await load();
  }, [load]);

  /**
   * Does the current user have `key`?
   * Fail-open until a successful payload arrives; platform admins and the "*"
   * wildcard always pass.
   */
  const has = useCallback(
    (key) => {
      if (!key) return true;
      if (!ready) return true; // fail open while loading / on bootstrap failure
      if (isPlatformAdmin) return true;
      if (permissions.includes('*')) return true;
      return permissions.includes(key);
    },
    [ready, isPlatformAdmin, permissions]
  );

  /** True if the user has ANY of the given keys (empty/undefined ⇒ ungated). */
  const hasAny = useCallback(
    (keys) => {
      if (!keys || keys.length === 0) return true;
      return keys.some((k) => has(k));
    },
    [has]
  );

  /** True if the user has ALL of the given keys. */
  const hasAll = useCallback(
    (keys) => {
      if (!keys || keys.length === 0) return true;
      return keys.every((k) => has(k));
    },
    [has]
  );

  /**
   * Does the current user hold partner key `key`? Fail-CLOSED (false until a
   * successful payload) and no platform-admin bypass — see the header comment.
   */
  const hasPartner = useCallback(
    (key) => ready && !!key && partnerPermissions.includes(key),
    [ready, partnerPermissions]
  );

  // Partner staff = members of a partner's hidden home company (§1.2).
  const isPartnerStaff = !!company?.is_partner_home;

  const value = useMemo(
    () => ({
      permissions,
      roles,
      isPlatformAdmin,
      company,
      entitlements,
      tier,
      partner,
      isPartnerAdmin,
      isPartnerStaff,
      partnerPermissions,
      lockout,
      ready,
      loading,
      error,
      has,
      hasAny,
      hasAll,
      hasPartner,
      refresh: load,
      retryAfterLockout,
    }),
    [
      permissions,
      roles,
      isPlatformAdmin,
      company,
      entitlements,
      tier,
      partner,
      isPartnerAdmin,
      isPartnerStaff,
      partnerPermissions,
      lockout,
      ready,
      loading,
      error,
      has,
      hasAny,
      hasAll,
      hasPartner,
      load,
      retryAfterLockout,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
